import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, Package, ArrowLeft, Calendar, Loader2, History, Eye } from 'lucide-react';
import { consumptionService, type IAssignedProduct, type IScheduledDates, type IConsumptionOrder, type IPurchaseHistory } from '@/services';

interface ConsumptionHistoryPageProps {}

const ConsumptionHistoryPage: React.FC<ConsumptionHistoryPageProps> = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();

  // Estados para datos
  const [consumptionOrders, setConsumptionOrders] = useState<IConsumptionOrder[]>([]);
  const [assignedProducts, setAssignedProducts] = useState<IAssignedProduct[]>([]);
  const [purchaseHistory, setPurchaseHistory] = useState<IPurchaseHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [childName, setChildName] = useState<string>('');

  // Estados para el modal de fechas programadas
  const [isScheduledDatesModalOpen, setIsScheduledDatesModalOpen] = useState(false);
  const [scheduledDatesData, setScheduledDatesData] = useState<IScheduledDates | null>(null);
  const [isLoadingScheduledDates, setIsLoadingScheduledDates] = useState(false);
  
  // Estado para la pestaña activa
  const [activeTab, setActiveTab] = useState('consumption');

  // Cargar datos del historial
  useEffect(() => {
    if (childId) {
      loadHistoryData();
    }
  }, [childId]);

  const loadHistoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar historial de consumo
      const consumptionData = await consumptionService.getHistoryByChildId(childId!);
      setConsumptionOrders(consumptionData);

      // Cargar productos asignados
      const productsData = await consumptionService.getAssignedProducts(childId!);
      setAssignedProducts(productsData);

      // Cargar historial de compras
      const purchaseData = await consumptionService.getPurchaseHistory(childId!);
      setPurchaseHistory(purchaseData);

      // Obtener nombre del hijo desde el localStorage o usar un placeholder
      // Por ahora usamos un placeholder, pero se puede implementar un servicio específico
      setChildName('Estudiante');

    } catch (err) {
      console.error('Error cargando datos del historial:', err);
      setError('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  // Calcular totales
  const totalCharges = 0; // Ya no calculamos cargas aquí
  const totalConsumptions = consumptionOrders.reduce((sum, order) => sum + order.total, 0);
  // const totalPurchases = purchaseHistory.reduce((sum, purchase) => sum + purchase.monto, 0);
  const totalInvested = 0; // Ya no calculamos inversión aquí

  // Manejar apertura del modal de fechas programadas
  const handleViewScheduledDates = async (product: IAssignedProduct) => {
    setIsScheduledDatesModalOpen(true);
    setIsLoadingScheduledDates(true);
    setScheduledDatesData(null);

    try {
      if (!childId) {
        throw new Error("ID del estudiante no válido");
      }

      if (!product.id) {
        throw new Error("ID del producto no válido");
      }

      const data = await consumptionService.getScheduledDates(childId, product.id);
      setScheduledDatesData(data);
    } catch (error) {
      console.error("Error al cargar fechas programadas:", error);
    } finally {
      setIsLoadingScheduledDates(false);
    }
  };

  // Formatear fecha
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL');
  };

  // Formatear monto
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  // Obtener nombre de la sección activa
  const getActiveSectionName = (tab: string) => {
    switch (tab) {
      case 'consumption':
        return 'Historial de Consumos';
      case 'assigned':
        return 'Productos Asignados';
      case 'purchases':
        return 'Historial de Compras';
      default:
        return 'Historial de Consumos';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Cargando historial...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadHistoryData}>Reintentar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto p-3 sm:p-6 max-w-6xl">
        {/* Hero Section - Copiado del dashboard del apoderado */}
        <div className="relative mb-6 sm:mb-8">
          {/* Fondo con gradiente y efectos */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-2xl sm:rounded-3xl blur-2xl sm:blur-3xl"></div>

          {/* Contenido del Hero */}
          <div className="relative bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl sm:shadow-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <History className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold text-foreground mb-1 sm:mb-2 leading-tight">
                  Historial de {childName}
                </h1>
                <p className="text-xs sm:text-sm lg:text-base text-muted-foreground leading-relaxed">
                  Revisa el historial completo de consumos y productos asignados
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard')}
                className="h-8 sm:h-9 hover:bg-primary/10 transition-colors duration-200 text-xs sm:text-sm"
              >
                <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="hidden sm:inline">Volver al Dashboard</span>
                <span className="sm:hidden">Volver</span>
              </Button>
            </div>

            {/* Estadísticas rápidas - Copiado del dashboard del apoderado */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Package className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                  <span className="text-xs sm:text-sm font-medium text-emerald-600 truncate">
                    Total Cargas
                  </span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-emerald-600">
                  {formatAmount(totalCharges)}
                </span>
              </div>

              <div className="bg-gradient-to-r from-rose-500/10 to-rose-600/5 border border-rose-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-rose-600" />
                  <span className="text-xs sm:text-sm font-medium text-rose-600 truncate">
                    Total Consumos
                  </span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-rose-600">
                  {formatAmount(totalConsumptions)}
                </span>
              </div>

              <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Package className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                  <span className="text-xs sm:text-sm font-medium text-primary truncate">
                    Productos Asignados
                  </span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-primary">
                  {assignedProducts.length}
                </span>
              </div>

              <div className="bg-gradient-to-r from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <History className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                  <span className="text-xs sm:text-sm font-medium text-purple-600 truncate">
                    Historial de Compras
                  </span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-600">
                  {purchaseHistory.length}
                </span>
              </div>

              <div className="bg-gradient-to-r from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Package className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                  <span className="text-xs sm:text-sm font-medium text-blue-600 truncate">
                    Total Invertido
                  </span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600">
                  {formatAmount(totalInvested)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Sistema de pestañas - Copiado del dashboard del apoderado */}
        <div className="space-y-6 sm:space-y-8">
          {/* Indicador de navegación */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span className="text-xs sm:text-sm text-muted-foreground font-medium">
                Navegación del Panel
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Sección activa:</span>
              <span className="font-medium text-primary">
                {getActiveSectionName(activeTab)}
              </span>
            </div>
          </div>

          <Tabs 
            value={activeTab} 
            onValueChange={setActiveTab}
            className="space-y-6 sm:space-y-8"
          >
            <TabsList className="grid w-full grid-cols-3 bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-xl sm:rounded-2xl p-1.5 shadow-lg min-h-[3.5rem] sm:min-h-[4rem]">
              <TabsTrigger 
                value="consumption" 
                className="flex items-center justify-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg rounded-lg sm:rounded-xl transition-all duration-300 text-xs sm:text-sm font-medium h-12 sm:h-14 hover:bg-primary/10 hover:scale-105 active:scale-95"
              >
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="hidden sm:inline">Historial de Consumos</span>
                <span className="sm:hidden">Consumos</span>
              </TabsTrigger>
              <TabsTrigger 
                value="assigned" 
                className="flex items-center justify-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg rounded-lg sm:rounded-xl transition-all duration-300 text-xs sm:text-sm font-medium h-12 sm:h-14 hover:bg-primary/10 hover:scale-105 active:scale-95"
              >
                <Package className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="hidden sm:inline">Productos Asignados</span>
                <span className="sm:hidden">Productos</span>
              </TabsTrigger>
              <TabsTrigger 
                value="purchases" 
                className="flex items-center justify-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg rounded-lg sm:rounded-xl transition-all duration-300 text-xs sm:text-sm font-medium h-12 sm:h-14 hover:bg-primary/10 hover:scale-105 active:scale-95"
              >
                <History className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="hidden sm:inline">Historial de Compras</span>
                <span className="sm:hidden">Compras</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab de Historial de Consumos */}
            <TabsContent value="consumption" className="space-y-6 sm:space-y-6 pt-8 sm:pt-4 lg:pt-2">
              <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl hover:shadow-2xl sm:hover:shadow-3xl transition-all duration-300 group">
                <CardHeader className="pb-4 sm:pb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                        <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                          Historial de Consumos
                        </CardTitle>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Revisa todas las transacciones de este estudiante
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 sm:space-y-6">
                  {consumptionOrders.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No hay pedidos consumidos</h3>
                      <p className="text-muted-foreground">Este estudiante aún no tiene pedidos consumidos en su historial</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">{consumptionOrders.length} pedidos consumidos</p>
                      
                      {/* Vista de escritorio */}
                      <div className="hidden md:block">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-medium text-foreground">Fecha</TableHead>
                              <TableHead className="font-medium text-foreground">Pedido</TableHead>
                              <TableHead className="font-medium text-foreground">Productos</TableHead>
                              <TableHead className="font-medium text-foreground">Total</TableHead>
                              <TableHead className="font-medium text-foreground">Estado</TableHead>
                              <TableHead className="font-medium text-foreground">Detalles</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {consumptionOrders.map((order, index) => (
                              <TableRow key={order.orderId || `order-${index}`} className="hover:bg-muted/30">
                                <TableCell className="text-muted-foreground">{formatDate(order.createdAt)}</TableCell>
                                <TableCell className="font-medium text-foreground">
                                  #{order.orderId.slice(0, 8)}...
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {order.items.length} productos
                                </TableCell>
                                <TableCell className="text-emerald-600 font-medium">
                                  {formatAmount(order.total)}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="default" className="bg-emerald-100 text-emerald-800">
                                    {order.status}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(`/dashboard/orders/${order.orderId}?context=consumption-history`)}
                                    className="h-8 sm:h-9 hover:bg-primary/10 transition-colors duration-200 text-xs sm:text-sm"
                                  >
                                    <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                    Ver Detalles
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Vista móvil */}
                      <div className="md:hidden space-y-4">
                        {consumptionOrders.map((order, index) => (
                          <Card key={order.orderId || `order-${index}`} className="border-border/50">
                            <CardContent className="p-4">
                              <div className="space-y-3">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-foreground">
                                      Pedido #{order.orderId.slice(0, 8)}...
                                    </p>
                                    <p className="text-sm text-muted-foreground">{formatDate(order.createdAt)}</p>
                                  </div>
                                  <Badge variant="default" className="bg-emerald-100 text-emerald-800">
                                    {order.status}
                                  </Badge>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">
                                    {order.items.length} productos
                                  </span>
                                  <span className="text-emerald-600 font-medium">
                                    {formatAmount(order.total)}
                                  </span>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {order.items.map(item => item.productName).join(', ')}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full h-8 sm:h-9 hover:bg-primary/10 transition-colors duration-200 text-xs sm:text-sm"
                                  onClick={() => navigate(`/dashboard/orders/${order.orderId}?context=consumption-history`)}
                                >
                                  <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                  Ver Detalles
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab de Productos Asignados */}
            <TabsContent value="assigned" className="space-y-6 sm:space-y-6 pt-8 sm:pt-4 lg:pt-2">
              <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl hover:shadow-2xl sm:hover:shadow-3xl transition-all duration-300 group">
                <CardHeader className="pb-4 sm:pb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                        <Package className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                          Productos Asignados
                        </CardTitle>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Gestiona los productos asignados a este estudiante
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 sm:space-y-6">
                  {assignedProducts.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No hay productos asignados</h3>
                      <p className="text-muted-foreground">Este estudiante aún no tiene productos asignados</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">{assignedProducts.length} productos asignados</p>
                      
                      {/* Vista de escritorio */}
                      <div className="hidden md:block">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-medium text-foreground">Producto</TableHead>
                              <TableHead className="font-medium text-foreground">Cantidad</TableHead>
                              <TableHead className="font-medium text-foreground">Precio Unitario</TableHead>
                              <TableHead className="font-medium text-foreground">Fecha Asignación</TableHead>
                              <TableHead className="font-medium text-foreground">Fechas Programadas</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {assignedProducts.map((product, index) => (
                              <TableRow key={product.id || `product-${index}`} className="hover:bg-muted/30">
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-foreground">{product.productoNombre}</p>
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground">{product.cantidadAsignada}</TableCell>
                                <TableCell className="text-muted-foreground">{formatAmount(product.precioUnitario)}</TableCell>
                                <TableCell className="text-muted-foreground">{formatDate(product.fechaAsignacion)}</TableCell>
                                <TableCell>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleViewScheduledDates(product)}
                                    className="h-8 sm:h-9 hover:bg-primary/10 transition-colors duration-200 text-xs sm:text-sm"
                                  >
                                    <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                    Ver Fechas
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Vista móvil */}
                      <div className="md:hidden space-y-4">
                        {assignedProducts.map((product, index) => (
                          <Card key={product.id || `product-${index}`} className="border-border/50">
                            <CardContent className="p-4">
                              <div className="space-y-3">
                                <div>
                                  <h3 className="font-medium text-foreground">{product.productoNombre}</h3>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Cantidad: {product.cantidadAsignada}</span>
                                  <span className="text-muted-foreground">{formatAmount(product.precioUnitario)}</span>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Asignado: {formatDate(product.fechaAsignacion)}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full h-8 sm:h-9 hover:bg-primary/10 transition-colors duration-200 text-xs sm:text-sm"
                                  onClick={() => handleViewScheduledDates(product)}
                                >
                                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                  Ver Fechas Programadas
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab de Historial de Compras */}
            <TabsContent value="purchases" className="space-y-6 sm:space-y-6 pt-8 sm:pt-4 lg:pt-2">
              <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl hover:shadow-2xl sm:hover:shadow-3xl transition-all duration-300 group">
                <CardHeader className="pb-4 sm:pb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                        <History className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                          Historial de Compras
                        </CardTitle>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Revisa todas las compras realizadas por este estudiante
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 sm:space-y-6">
                  {purchaseHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No hay compras registradas</h3>
                      <p className="text-muted-foreground">Este estudiante aún no ha realizado compras</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">{purchaseHistory.length} compras realizadas</p>
                      
                      {/* Vista de escritorio */}
                      <div className="hidden md:block">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-medium text-foreground">Fecha</TableHead>
                              <TableHead className="font-medium text-foreground">Producto</TableHead>
                              <TableHead className="font-medium text-foreground">Descripción</TableHead>
                              <TableHead className="font-medium text-foreground">Monto</TableHead>
                              <TableHead className="font-medium text-foreground">Acciones</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {purchaseHistory.map((purchase, index) => (
                              <TableRow key={purchase.id || `purchase-${index}`} className="hover:bg-muted/30">
                                <TableCell className="text-muted-foreground">{formatDate(purchase.fecha)}</TableCell>
                                <TableCell className="font-medium text-foreground">
                                  {purchase.producto}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {purchase.descripcion}
                                </TableCell>
                                <TableCell className="text-purple-600 font-medium">
                                  {formatAmount(purchase.monto)}
                                </TableCell>
                                <TableCell>
                                  {purchase.pedidoId && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => navigate(`/dashboard/orders/${purchase.pedidoId}`)}
                                      className="h-7 px-2 text-xs"
                                    >
                                      <Eye className="h-3 w-3 mr-1" />
                                      Ver Detalle
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Vista móvil */}
                      <div className="md:hidden space-y-4">
                        {purchaseHistory.map((purchase, index) => (
                          <Card key={purchase.id || `purchase-${index}`} className="border-border/50">
                            <CardContent className="p-4">
                              <div className="space-y-3">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-foreground">
                                      {purchase.producto}
                                    </p>
                                    <p className="text-sm text-muted-foreground">{formatDate(purchase.fecha)}</p>
                                  </div>
                                  <span className="text-purple-600 font-medium">
                                    {formatAmount(purchase.monto)}
                                  </span>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {purchase.descripcion}
                                </div>
                                {purchase.pedidoId && (
                                  <div className="flex justify-end">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => navigate(`/dashboard/orders/${purchase.pedidoId}`)}
                                      className="h-8 px-3 text-xs"
                                    >
                                      <Eye className="h-3 w-3 mr-1" />
                                      Ver Detalle
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

      {/* Modal de Fechas Programadas */}
      <Dialog open={isScheduledDatesModalOpen} onOpenChange={setIsScheduledDatesModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Fechas Programadas</DialogTitle>
          </DialogHeader>

          {isLoadingScheduledDates ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="ml-2">Cargando fechas programadas...</span>
            </div>
          ) : scheduledDatesData ? (
            <div className="space-y-6">
              {/* Resumen */}
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <div className="w-1 h-3 bg-blue-500 rounded-full"></div>
                    Resumen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5">
                  <div className="flex justify-between items-center p-1.5 bg-white rounded text-xs">
                    <span className="font-medium text-gray-700">Total de Pedidos:</span>
                    <span className="font-semibold text-blue-600">{scheduledDatesData.summary.totalOrders}</span>
                  </div>
                  {scheduledDatesData.summary.firstDate && (
                    <div className="flex justify-between items-center p-1.5 bg-white rounded text-xs">
                      <span className="font-medium text-gray-700">Primera Fecha:</span>
                      <span className="font-semibold text-gray-900">{formatDate(scheduledDatesData.summary.firstDate)}</span>
                    </div>
                  )}
                  {scheduledDatesData.summary.lastDate && (
                    <div className="flex justify-between items-center p-1.5 bg-white rounded text-xs">
                      <span className="font-medium text-gray-700">Última Fecha:</span>
                      <span className="font-semibold text-gray-900">{formatDate(scheduledDatesData.summary.lastDate)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Fechas programadas */}
              {scheduledDatesData.scheduledDates.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
                    <h3 className="text-lg font-semibold text-gray-800">Pedidos Programados</h3>
                  </div>
                  <div className="space-y-2">
                    {scheduledDatesData.scheduledDates.map((dateGroup, dateIndex) => (
                      <div key={dateIndex} className="space-y-2">
                        {dateGroup.orders.map((order, orderIndex) => (
                          <div key={orderIndex} className="bg-white rounded-lg p-3 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                <span className="font-medium text-gray-900 text-sm">
                                  Pedido #{order.orderId.slice(0, 8)}...
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge 
                                  variant={order.status === 'EN_PREPARACION' ? 'default' : 'secondary'}
                                  className={`text-xs ${
                                    order.status === 'COMPLETADO' 
                                      ? 'bg-green-100 text-green-800' 
                                      : order.status === 'EN_PREPARACION'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {order.status}
                                </Badge>
                                {order.deliveryDate && (
                                  <div className="hidden sm:flex items-center gap-1 text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                                    <Calendar className="h-3 w-3" />
                                    <span className="font-medium">{formatDate(order.deliveryDate)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <div className="flex items-center gap-3 text-xs text-gray-600">
                                <span><span className="font-medium">Total:</span> {formatAmount(order.total)}</span>
                                <span><span className="font-medium">Items:</span> {order.items.length}</span>
                                {order.deliveryDate && (
                                  <span className="sm:hidden flex items-center gap-1 text-gray-600">
                                    <Calendar className="h-3 w-3" />
                                    <span className="font-medium">{formatDate(order.deliveryDate)}</span>
                                  </span>
                                )}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700 hover:text-blue-800 transition-colors self-start sm:self-auto"
                                onClick={() => navigate(`/dashboard/orders/${order.orderId}?context=scheduled-dates`)}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                Ver Detalles
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-8">
                    <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hay fechas programadas</h3>
                    <p className="text-gray-600">No se encontraron pedidos programados para esta asignación</p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">No se pudieron cargar las fechas programadas</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  </div>
  );
};

export default ConsumptionHistoryPage;
